import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Popup } from "@/Common/Components/Modals/Popup";
import { Locale } from "@/Common/Models/Localization/Locale";
import { OntarioPatientEnrollmentModel } from "@/Common/Models/Patient/PatientEnrollment/Ontario/OntarioPatientEnrollmentModel";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { OntarioPatientEnrollmentItem } from "./EnrollmentComponents/OntarioPatientEnrollmentItem";
import { OntarioPatientEnrollmentPopup, OntarioPatientEnrollmentPopupType } from "./EnrollmentComponents/OntarioPatientEnrollmentPopup";
import { OntarioPatientEnrollmentStatusSection } from "./EnrollmentComponents/OntarioPatientEnrollmentStatusSection";
import { OntarioPatientEnrollmentSectionContainer } from "./OntarioPatientEnrollmentSectionContainer";

interface IOntarioPatientEnrollmentProps {
    patientId: string;
    getPhysician: (contactFacetId: string) => Person;
}

class OntarioPatientEnrollmentState {
    public currentEnrollment: OntarioPatientEnrollmentModel = null;
    public historicalEnrollments: OntarioPatientEnrollmentModel[] = [];
    public displayPatientEnrollmentPopupType: OntarioPatientEnrollmentPopupType = OntarioPatientEnrollmentPopupType.None;
    public popupEnrollmentModel: OntarioPatientEnrollmentModel = null;
    public currentDeletingEnrollmentId: string = null;
    public isLoading: boolean = false;

    public constructor(fields?: Partial<OntarioPatientEnrollmentState>) {
        Object.assign(this, fields);
    }
}

class OntarioPatientEnrollment extends BaseReactComponent<IOntarioPatientEnrollmentProps, OntarioPatientEnrollmentState> {
    public constructor(props: IOntarioPatientEnrollmentProps) {
        super(props);
        this.state = new OntarioPatientEnrollmentState();
    }

    public componentWillMount(): void {
        this.setAcceptableLocales([Locale.en, Locale.fr]);
        this.getEnrollments();
    }

    public render(): JSX.Element {
        if (!cacheStore.AreTranslationsLoaded(Locale.en))
            return null;

        const enrollmentPopupType = this.state.displayPatientEnrollmentPopupType;

        return (
            <div className="patient-prof ontario-patient-enrollment">
                <div className="scrollable">
                    {enrollmentPopupType !== OntarioPatientEnrollmentPopupType.None && (
                        <OntarioPatientEnrollmentPopup
                            enrollmentPopupType={enrollmentPopupType}
                            patientEnrollmentModel={this.state.popupEnrollmentModel}
                            onClose={() => this.mutateState(s => {
                                s.displayPatientEnrollmentPopupType = OntarioPatientEnrollmentPopupType.None;
                                s.popupEnrollmentModel = null;
                            })}
                            onSaveEnrollment={patientEnrollment => {
                                this.mutateState(
                                    s => s.isLoading = true,
                                    () => {
                                        if (enrollmentPopupType === OntarioPatientEnrollmentPopupType.Terminate)
                                            this.terminateEnrollment(patientEnrollment);
                                        else
                                            this.saveEnrollment(patientEnrollment);
                                    }
                                );
                            }}
                            getPhysician={this.props.getPhysician}
                            isLoading={this.state.isLoading}
                        />
                    )}
                    <OntarioPatientEnrollmentStatusSection
                        currentEnrollment={this.state.currentEnrollment}
                        onEnrollPatient={() => this.mutateState(s => s.displayPatientEnrollmentPopupType = OntarioPatientEnrollmentPopupType.EditCurrentOrEnroll)}
                        onTerminatePatientEnrollment={() =>
                            this.mutateState(s => {
                                s.displayPatientEnrollmentPopupType = OntarioPatientEnrollmentPopupType.Terminate;
                                s.popupEnrollmentModel = this.state.currentEnrollment;
                            })
                        }
                        getPhysician={contactFacetId => this.props.getPhysician(contactFacetId)}
                    />
                    {this.state.currentDeletingEnrollmentId && this.renderDeletePopup()}
                    {this.state.currentEnrollment && (
                        <OntarioPatientEnrollmentSectionContainer title={this.getString("EnrolmentLabel")}>
                            <OntarioPatientEnrollmentItem
                                enrollment={this.state.currentEnrollment}
                                onEdit={() => this.onEditEnrollment(this.state.currentEnrollment)}
                                getPhysician={this.props.getPhysician}
                            />
                        </OntarioPatientEnrollmentSectionContainer>
                    )}
                    {this.state.historicalEnrollments?.length > 0 && (
                        <OntarioPatientEnrollmentSectionContainer title={this.getString("EnrolmentHistoryLabel")}>
                            {this.state.historicalEnrollments.map(enrollment => (
                                <OntarioPatientEnrollmentItem
                                    key={enrollment.Id}
                                    enrollment={enrollment}
                                    onEdit={() => this.onEditEnrollment(enrollment)}
                                    onDelete={() => this.onDeleteEnrollment(enrollment)}
                                    getPhysician={this.props.getPhysician}
                                />
                            ))}
                        </OntarioPatientEnrollmentSectionContainer>
                    )}
                </div>
            </div>
        );
    }

    private initState(patientEnrollments: OntarioPatientEnrollmentModel[]): void {
        const enrollments = patientEnrollments?.map(p => new OntarioPatientEnrollmentModel(p)) ?? [];
        const currentEnrollment = enrollments.firstOrDefault(p => p.isCurrentEnrollment());
        const historicalEnrollments = enrollments
            .filter(p => !p.isCurrentEnrollment())
            .sortByPropertyDesc(p => p.EnrollmentEndDate);

        this.mutateState(s => {
            s.currentEnrollment = currentEnrollment;
            s.historicalEnrollments = historicalEnrollments;
            s.currentDeletingEnrollmentId = null;
            s.displayPatientEnrollmentPopupType = OntarioPatientEnrollmentPopupType.None;
            s.popupEnrollmentModel = null;
            s.isLoading = false;
        });

        const enrollmentStatus = currentEnrollment ?? historicalEnrollments?.firstOrDefault() ?? [];
        sessionStore.dispatch(LocalOperations.LabsUI.SetOntarioEnrollmentPatientBanner, { enrollmentStatus: enrollmentStatus });
        sessionStore.dispatch(LocalPatientOperations.Profile.Enrollment_SetOntarioEnrollmentPatientBanner, { enrollmentStatus: enrollmentStatus });
    }

    private renderDeletePopup(): JSX.Element {
        return (
            <Popup
                title={this.getString("DeleteEnrolmentLabel")}
                onCloseDelegate={() => this.mutateState(s => s.currentDeletingEnrollmentId = null)}
                manageOverlay
                titleLowerCase
                standardFooterProps={{
                    okButton: { onClick: () => this.deleteEnrollment(this.state.currentDeletingEnrollmentId) }
                }}
                width={500}
                acceptableLanguages={[Locale.en, Locale.fr]}
            >
                <p className="confirm-delete-question-popup">
                    {this.getString("DeleteEnrolmentConfirmQuestionLabel")}
                </p>
            </Popup>
        );
    }

    private onDeleteEnrollment(currentEnrollment: OntarioPatientEnrollmentModel): void {
        this.mutateState(s => s.currentDeletingEnrollmentId = currentEnrollment.Id);
    }

    private onEditEnrollment(enrollment: OntarioPatientEnrollmentModel): void {
        this.mutateState(s => {
            s.displayPatientEnrollmentPopupType = enrollment.isCurrentEnrollment()
                ? OntarioPatientEnrollmentPopupType.EditCurrentOrEnroll
                : OntarioPatientEnrollmentPopupType.EditPast;
            s.popupEnrollmentModel = enrollment;
        });
    }

    private async deleteEnrollment(enrollmentId: string): Promise<void> {
        this.handleResponse(
            await Client.postAsync<{ PatientEnrollments: OntarioPatientEnrollmentModel[] }>(
                ServerOperations.Server.Myle_OntarioPatientEnrollment_DeleteEnrollment,
                {
                    EnrollmentId: enrollmentId,
                    PatientId: this.props.patientId
                }
            )
        );
    }

    private async getEnrollments(): Promise<void> {
        this.handleResponse(
            await Client.postAsync<{ PatientEnrollments: OntarioPatientEnrollmentModel[] }>(
                ServerOperations.Server.Myle_OntarioPatientEnrollment_GetEnrollmentsForPatient,
                { PatientId: this.props.patientId }
            )
        );
    }

    private async saveEnrollment(patientEnrollment: OntarioPatientEnrollmentModel): Promise<void> {
        this.handleResponse(
            await Client.postAsync<{ PatientEnrollments: OntarioPatientEnrollmentModel[] }>(
                ServerOperations.Server.Myle_OntarioPatientEnrollment_UpsertEnrollment,
                {
                    OntarioPatientEnrollmentModel: patientEnrollment,
                    PatientId: this.props.patientId
                }
            )
        );
    }

    private async terminateEnrollment(patientEnrollment: OntarioPatientEnrollmentModel): Promise<void> {
        this.handleResponse(
            await Client.postAsync<{ PatientEnrollments: OntarioPatientEnrollmentModel[] }>(
                ServerOperations.Server.Myle_OntarioPatientEnrollment_TerminateEnrollment,
                {
                    OntarioPatientEnrollmentModel: patientEnrollment,
                    PatientId: this.props.patientId
                }
            )
        );
    }

    private handleResponse(result: ClientResponse<{ PatientEnrollments: OntarioPatientEnrollmentModel[]; }>): void {
        if (!result?.isSuccess) {
            sessionStore.showPopup(result.errors);
        }

        if (result?.data?.PatientEnrollments != null) {
            this.initState(result.data.PatientEnrollments);
        }
    }
}

export { OntarioPatientEnrollment };
